import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, MessageSquare, Camera, ArrowLeft, Send, RotateCcw, Check, X, Plus } from 'lucide-react';
import { useGoBack } from '../../hooks/useGoBack';
import { FotoThumbnail } from '../../components/shared/FotoThumbnail';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Skeleton } from '../../components/ui/Skeleton';
import { StatusBadge } from '../../components/ui/Badge';
import {
  usePlanillaDetail,
  useReturnToResidente,
  useSendToGerente,
  useCreateComment,
  usePlanillaProgress,
  useApprovePlanilla,
  useRejectPlanilla,
  useCreateDescuento,
} from '../../hooks/usePlanillas';
import type { PlanillaDetailDTO, PlanillaProgressItem } from '../../hooks/usePlanillas';
import { useAuthStore } from '../../stores/auth-store';
import { toast } from '../../components/ui/Toast';

// ── Helpers ──────────────────────────────────────────────────────────

function descuentoTipoLabel(tipo: string): string {
  if (tipo === 'multa') return 'Descuento de Obra';
  if (tipo === 'administrativo') return 'Descuento Administrativo';
  // Capitalize first letter for other types
  return tipo.charAt(0).toUpperCase() + tipo.slice(1);
}

export function AdminReviewPage() {
  const { planillaId } = useParams<{ planillaId: string }>();
  const navigate = useNavigate();
  const goBack = useGoBack('/planillas');
  const { user } = useAuthStore();
  const userRole = user?.role ?? '';

  const { data: planilla, isLoading } = usePlanillaDetail(planillaId ?? '');
  const { data: projectProgress } = usePlanillaProgress(planilla?.project_id ?? '');
  const returnToResidente = useReturnToResidente(planillaId ?? '');
  const sendToGerente = useSendToGerente(planillaId ?? '');
  const createComment = useCreateComment(planillaId ?? '');
  const approvePlanilla = useApprovePlanilla(planillaId ?? '');
  const rejectPlanilla = useRejectPlanilla(planillaId ?? '');
  const createDescuento = useCreateDescuento(planillaId ?? '');

  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnComment, setReturnComment] = useState('');
  const [newComment, setNewComment] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectMotivo, setRejectMotivo] = useState('');

  // Admin descuento state
  const [showAdminDescuento, setShowAdminDescuento] = useState(false);
  const [adminDescuentoDesc, setAdminDescuentoDesc] = useState('');
  const [adminDescuentoMonto, setAdminDescuentoMonto] = useState('');
  const [adminDescuentoMaestroId, setAdminDescuentoMaestroId] = useState('');

  const handleReturn = async () => {
    if (returnComment.trim().length === 0) {
      toast('error', 'Ingrese el motivo de la devolución');
      return;
    }
    try {
      await returnToResidente.mutateAsync(returnComment);
      setShowReturnModal(false);
      toast('success', 'Planilla devuelta al residente');
      navigate('/planillas');
    } catch {
      toast('error', 'Error al devolver planilla');
    }
  };

  const handleSendToGerente = async () => {
    try {
      await sendToGerente.mutateAsync();
      toast('success', `Planilla N°${planilla?.numero} enviada a gerencia para aprobación`);
      navigate('/planillas');
    } catch {
      toast('error', 'Error al enviar a gerencia. Verifique que todos los extras tengan costo asignado.');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await createComment.mutateAsync(newComment.trim());
      setNewComment('');
      toast('success', 'Comentario agregado');
    } catch {
      toast('error', 'Error al agregar comentario');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4 fade-up">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-60 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!planilla) {
    return <div className="p-4 text-center text-red-600">Planilla no encontrada</div>;
  }

  const isReviewable = planilla.estado === 'pendiente_administrativo';

  // Group details by maestro
  const maestroIds = [...new Set(planilla.details.map((d) => d.maestro_id))];
  const maestroNameMap = new Map<string, string>();
  for (const d of planilla.details) maestroNameMap.set(d.maestro_id, d.maestro_nombre);
  for (const m of planilla.maestros) maestroNameMap.set(m.maestro_id, m.nombre);

  return (
    <div className="space-y-4 fade-up pb-24">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={goBack} className="text-gray-400 hover:text-gray-600">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-brand-dark">
            Revisión — Planilla N°{planilla.numero}
          </h1>
          <p className="text-xs text-gray-500">
            {planilla.project_nombre} · {planilla.fecha_desde} — {planilla.fecha_hasta} · Creada por {planilla.created_by.nombre}
          </p>
        </div>
        <StatusBadge entity="planilla" status={planilla.estado} />
      </div>

      {/* Project-level progress context */}
      {projectProgress && projectProgress.length > 0 && (
        <ProjectProgressCard progress={projectProgress} />
      )}

      {/* Per-maestro review tables */}
      {maestroIds.map((maestroId) => (
        <MaestroReviewTable
          key={maestroId}
          maestroId={maestroId}
          maestroNombre={maestroNameMap.get(maestroId) ?? 'Maestro'}
          planilla={planilla}
        />
      ))}

      {/* Servicios Varios */}
      {planilla.extras.length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Servicios Varios</h3>
          <div className="space-y-2">
            {planilla.extras.map((e) => (
              <div key={e.id} className="flex justify-between items-start text-sm">
                <div className="flex-1">
                  <p className="text-gray-700">{e.descripcion}</p>
                  {e.fotos.length > 0 && (
                    <p className="text-xs text-gray-400">{e.fotos.length} foto(s)</p>
                  )}
                </div>
                <span className="font-medium text-green-700">+${e.monto.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Descuentos */}
      {planilla.descuentos.length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Descuentos</h3>
          <div className="space-y-2">
            {planilla.descuentos.map((d) => (
              <div key={d.id} className="flex justify-between text-sm">
                <div>
                  <p className="text-gray-700">{d.descripcion}</p>
                  <p className="text-xs text-gray-400">{descuentoTipoLabel(d.tipo)}</p>
                </div>
                <span className="font-medium text-red-600">-${d.monto.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Descuento Administrativo — only visible when reviewable */}
      {isReviewable && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700">Descuento Administrativo</h3>
            {!showAdminDescuento && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setShowAdminDescuento(true)}
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                Agregar Descuento Administrativo
              </Button>
            )}
          </div>
          {showAdminDescuento && (
            <div className="space-y-3 pt-2 border-t border-gray-100">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Maestro</label>
                <select
                  value={adminDescuentoMaestroId}
                  onChange={(e) => setAdminDescuentoMaestroId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
                >
                  <option value="">Seleccionar maestro...</option>
                  {planilla.maestros.map((m) => (
                    <option key={m.maestro_id} value={m.maestro_id}>{m.nombre}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Descripción</label>
                <input
                  type="text"
                  value={adminDescuentoDesc}
                  onChange={(e) => setAdminDescuentoDesc(e.target.value)}
                  placeholder="Descripción del descuento..."
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Monto ($)</label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={adminDescuentoMonto}
                  onChange={(e) => setAdminDescuentoMonto(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setShowAdminDescuento(false);
                    setAdminDescuentoDesc('');
                    setAdminDescuentoMonto('');
                    setAdminDescuentoMaestroId('');
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  isLoading={createDescuento.isPending}
                  disabled={!adminDescuentoMaestroId || !adminDescuentoDesc.trim() || !adminDescuentoMonto || parseFloat(adminDescuentoMonto) <= 0}
                  onClick={async () => {
                    try {
                      await createDescuento.mutateAsync({
                        tipo: 'administrativo',
                        descripcion: adminDescuentoDesc.trim(),
                        monto: parseFloat(adminDescuentoMonto),
                        maestroId: adminDescuentoMaestroId,
                      });
                      setAdminDescuentoDesc('');
                      setAdminDescuentoMonto('');
                      setAdminDescuentoMaestroId('');
                      setShowAdminDescuento(false);
                      toast('success', 'Descuento administrativo agregado');
                    } catch {
                      toast('error', 'Error al agregar descuento');
                    }
                  }}
                >
                  Guardar
                </Button>
              </div>
            </div>
          )}
          {!showAdminDescuento && planilla.descuentos.filter((d) => d.tipo === 'administrativo').length === 0 && (
            <p className="text-xs text-gray-400 italic">Sin descuentos administrativos registrados</p>
          )}
        </Card>
      )}

      {/* Photo grid */}
      {planilla.fotos.length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Camera className="w-4 h-4" />
            Fotos de evidencia ({planilla.fotos.length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {planilla.fotos.map((f) => (
              <FotoThumbnail key={f.id} s3Key={f.s3_key} filename={f.filename} />
            ))}
          </div>
        </Card>
      )}

      {/* Comment thread */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <MessageSquare className="w-4 h-4" />
          Comentarios ({planilla.comments.length})
        </h3>
        <div className="space-y-3 max-h-60 overflow-y-auto">
          {planilla.comments.map((c) => (
            <div key={c.id} className="text-sm">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="font-medium text-gray-700">{c.created_by.nombre}</span>
                <span className="text-xs text-gray-400">{new Date(c.created_at).toLocaleString('es-EC')}</span>
              </div>
              <p className="text-gray-600 whitespace-pre-wrap">{c.mensaje}</p>
            </div>
          ))}
          {planilla.comments.length === 0 && (
            <p className="text-xs text-gray-400 italic">Sin comentarios</p>
          )}
        </div>
        <div className="flex gap-2 mt-3 pt-3 border-t">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Agregar comentario..."
            className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg"
            onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
          />
          <Button size="sm" onClick={handleAddComment} isLoading={createComment.isPending} disabled={!newComment.trim()}>
            Enviar
          </Button>
        </div>
      </Card>

      {/* Totals */}
      <Card className="p-4">
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Total rubros</span>
            <span className="font-medium">${planilla.totals.total_rubros.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Servicios Varios</span>
            <span className="font-medium text-green-700">+${planilla.totals.total_extras.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between text-red-600">
            <span>Total descuentos</span>
            <span className="font-medium">-${planilla.totals.total_descuentos.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between border-t pt-2 font-bold text-lg text-brand-dark">
            <span>Total neto</span>
            <span>${planilla.totals.total_neto.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>
      </Card>

      {/* Action buttons — admin review (pendiente_administrativo) */}
      {isReviewable && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-20">
          <div className="max-w-4xl mx-auto flex gap-3 justify-end">
            <Button variant="danger" onClick={() => setShowReturnModal(true)}>
              <RotateCcw className="w-4 h-4 mr-1" />
              Devolver al residente
            </Button>
            <Button onClick={handleSendToGerente} isLoading={sendToGerente.isPending}>
              <Send className="w-4 h-4 mr-1" />
              Enviar a gerencia
            </Button>
          </div>
        </div>
      )}

      {/* Action buttons — gerente/operaciones approval (revisada) */}
      {planilla && planilla.estado === 'revisada' && ['gerente', 'operaciones'].includes(userRole) && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-20">
          <div className="max-w-4xl mx-auto flex gap-3 justify-end">
            <Button variant="danger" onClick={() => setShowRejectModal(true)}>
              <X className="w-4 h-4 mr-1" />
              Rechazar
            </Button>
            <Button
              onClick={async () => {
                try {
                  await approvePlanilla.mutateAsync();
                  toast('success', `Planilla N°${planilla.numero} aprobada`);
                  navigate('/planillas');
                } catch {
                  toast('error', 'Error al aprobar planilla');
                }
              }}
              isLoading={approvePlanilla.isPending}
            >
              <Check className="w-4 h-4 mr-1" />
              Aprobar Planilla
            </Button>
          </div>
        </div>
      )}

      {/* Reject modal (gerente/operaciones) */}
      <Modal isOpen={showRejectModal} onClose={() => setShowRejectModal(false)} title="Rechazar Planilla" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Indique el motivo del rechazo (mínimo 10 caracteres):</p>
          <textarea
            value={rejectMotivo}
            onChange={(e) => setRejectMotivo(e.target.value)}
            rows={3}
            placeholder="Motivo del rechazo..."
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none"
          />
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => setShowRejectModal(false)}>Cancelar</Button>
            <Button
              variant="danger"
              disabled={rejectMotivo.length < 10}
              isLoading={rejectPlanilla.isPending}
              onClick={async () => {
                try {
                  await rejectPlanilla.mutateAsync(rejectMotivo);
                  setShowRejectModal(false);
                  toast('success', 'Planilla rechazada');
                  navigate('/planillas');
                } catch {
                  toast('error', 'Error al rechazar planilla');
                }
              }}
            >
              Rechazar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Return modal */}
      <Modal isOpen={showReturnModal} onClose={() => setShowReturnModal(false)} title="Devolver planilla al residente">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Indique el motivo de la corrección:</p>
          <textarea
            value={returnComment}
            onChange={(e) => setReturnComment(e.target.value)}
            rows={4}
            placeholder="Describir qué debe corregir el residente..."
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none"
          />
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => setShowReturnModal(false)}>Cancelar</Button>
            <Button
              variant="danger"
              onClick={handleReturn}
              isLoading={returnToResidente.isPending}
              disabled={returnComment.trim().length === 0}
            >
              Devolver
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Project Progress Card ────────────────────────────────────────────

export function ProjectProgressCard({ progress }: { progress: PlanillaProgressItem[] }) {
  return (
    <Card className="p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-1">Avance del proyecto</h3>
      <p className="text-xs text-gray-400 mb-3">Total acumulado en todas las planillas aprobadas y en revisión</p>
      <div className="space-y-3">
        {progress.map((item) => (
          <div key={item.recipe_id}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-gray-700 truncate flex-1 mr-2">{item.recipe_nombre}</span>
              <span className={`whitespace-nowrap font-medium ${item.porcentaje >= 100 ? 'text-green-600' : 'text-gray-500'}`}>
                {item.cantidad_registrada.toLocaleString('es-EC', { maximumFractionDigits: 2 })}
                {' / '}
                {item.cantidad_ot.toLocaleString('es-EC', { maximumFractionDigits: 2 })}
                {' '}{item.unidad}
                {' · '}
                <span className={item.porcentaje >= 100 ? 'text-green-600 font-bold' : item.porcentaje >= 80 ? 'text-orange-500' : ''}>
                  {item.porcentaje.toFixed(0)}%
                </span>
              </span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${item.porcentaje >= 100 ? 'bg-green-500' : item.porcentaje >= 80 ? 'bg-orange-400' : 'bg-blue-400'}`}
                style={{ width: `${Math.min(item.porcentaje, 100)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Maestro Review Table (C-30) ─────────────────────────────────────

function MaestroReviewTable({
  maestroId,
  maestroNombre,
  planilla,
}: {
  maestroId: string;
  maestroNombre: string;
  planilla: PlanillaDetailDTO;
}) {
  const [expanded, setExpanded] = useState(true);

  const details = planilla.details.filter((d) => d.maestro_id === maestroId);
  const descuentos = planilla.descuentos.filter((d) => d.maestro_id === maestroId);
  const subtotalRubros = details.reduce((sum, d) => sum + d.subtotal, 0);
  const totalDescuentos = descuentos.reduce((sum, d) => sum + d.monto, 0);
  const neto = subtotalRubros - totalDescuentos;

  // MDO lookup: rates per recipe for this maestro
  const maestroRates = planilla.maestros.find((m) => m.maestro_id === maestroId)?.rates ?? [];

  return (
    <Card className="overflow-hidden">
      <button
        className="w-full flex items-center justify-between p-4 text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <p className="text-sm font-semibold text-gray-800">{maestroNombre}</p>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-brand-dark">${neto.toFixed(2)}</span>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100">
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Trabajo / MDO</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Cantidad</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Unidad</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Precio</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-gray-500">Total</th>
                </tr>
              </thead>
              <tbody>
                {details.map((d) => {
                  const mdoItems = maestroRates.find((r) => r.recipe_id === d.recipe_id)?.mdo_items ?? [];
                  return (
                    <>
                      <tr key={d.id} className="border-t border-gray-100">
                        <td className="px-4 py-2 text-gray-700 font-medium">
                          {d.recipe_nombre}
                          {d.cabecera_nombre && (
                            <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-normal">{d.cabecera_nombre}</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right">{d.cantidad}</td>
                        <td className="px-4 py-2 text-right text-gray-500">{d.unidad}</td>
                        <td className="px-4 py-2 text-right">${d.precio_unitario.toFixed(2)}</td>
                        <td className="px-4 py-2 text-right font-medium">${d.subtotal.toFixed(2)}</td>
                      </tr>
                      {mdoItems.map((mdo) => (
                        <tr key={mdo.material_id} className="bg-blue-50/40">
                          <td className="px-4 py-1 pl-9 text-blue-700 text-xs italic">{mdo.material_nombre}</td>
                          <td colSpan={2} />
                          <td className="px-4 py-1 text-right text-blue-600 text-xs">${mdo.precio_unitario.toFixed(2)}</td>
                          <td />
                        </tr>
                      ))}
                    </>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 font-medium">
                  <td colSpan={4} className="px-4 py-2 text-right text-gray-600">Subtotal rubros</td>
                  <td className="px-4 py-2 text-right">${subtotalRubros.toFixed(2)}</td>
                </tr>
                {totalDescuentos > 0 && (
                  <tr className="bg-gray-50 text-red-600">
                    <td colSpan={4} className="px-4 py-2 text-right">Descuentos</td>
                    <td className="px-4 py-2 text-right font-medium">-${totalDescuentos.toFixed(2)}</td>
                  </tr>
                )}
                <tr className="bg-gray-50 font-bold text-brand-dark">
                  <td colSpan={4} className="px-4 py-2 text-right">Neto maestro</td>
                  <td className="px-4 py-2 text-right">${neto.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden px-4 pb-4 pt-3 space-y-3">
            {details.map((d) => {
              const mdoItems = maestroRates.find((r) => r.recipe_id === d.recipe_id)?.mdo_items ?? [];
              return (
                <div key={d.id}>
                  <div className="flex justify-between text-sm">
                    <div>
                      <p className="text-gray-700 font-medium">
                        {d.recipe_nombre}
                        {d.cabecera_nombre && (
                          <span className="ml-1 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-normal">{d.cabecera_nombre}</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-400">{d.cantidad} {d.unidad} × ${d.precio_unitario.toFixed(2)}</p>
                    </div>
                    <span className="font-medium">${d.subtotal.toFixed(2)}</span>
                  </div>
                  {mdoItems.length > 0 && (
                    <div className="mt-1 pl-4 space-y-0.5 border-l-2 border-blue-100">
                      {mdoItems.map((mdo) => (
                        <div key={mdo.material_id} className="flex justify-between text-xs text-blue-600">
                          <span className="italic">{mdo.material_nombre}</span>
                          <span>${mdo.precio_unitario.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="pt-2 border-t text-xs text-gray-500 space-y-1">
              <div className="flex justify-between"><span>Subtotal</span><span>${subtotalRubros.toFixed(2)}</span></div>
              {totalDescuentos > 0 && <div className="flex justify-between text-red-600"><span>Descuentos</span><span>-${totalDescuentos.toFixed(2)}</span></div>}
              <div className="flex justify-between font-bold text-brand-dark"><span>Neto</span><span>${neto.toFixed(2)}</span></div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

